import { useState, useRef, useEffect, useMemo } from 'react';
import { Container, Form, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import './ConfirmAddress.css';
import { en } from '../assets/labels-en';
import CustomForm from '../components/forms/CustomForm';
import CustomModal from '../components/common/CustomModal';
import Banner from '../components/Banner';
import DollarLogo from '../assets/images/banner-logo-dollar.svg';
import WarningIcon from '../assets/images/warning-rounded.svg';
import CustomButton from '../components/common/CustomButton';
import TermsOfUse from '../utils/TermsOfUse';
import { useTenant } from '../context/TenantContext';
import { requestCheque } from '../services/escheatmentservice';
import LoaderComponent from '../components/common/LoaderComponent';

const ConfirmAddress = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantSlug } = useTenant();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    addressLineTwo: '',
    city: '',
    state: '',
    postalCode: '',
  });
  const [ownershipChecked, setOwnershipChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [canAgree, setCanAgree] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(true);
  const termsContentRef = useRef(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const formRef = useRef(null);
  const [jwtToken, setJwtToken] = useState('');

  const handleShowTerms = (e) => {
    e.preventDefault();
    setShowTermsModal(true);
    setCanAgree(false);
    setShowScrollButton(true);
  };

  const handleCloseTerms = () => {
    setShowTermsModal(false);
  };

  const handleAgreeTerms = () => {
    setTermsChecked(true);
    setShowTermsModal(false);
  };

  const handleScrollToBottom = () => {
    if (termsContentRef.current) {
      termsContentRef.current.scrollTo({
        top: termsContentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = () => {
    if (termsContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = termsContentRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

      // Enable agree button when scrolled to bottom (within 50px)
      if (isNearBottom) {
        setCanAgree(true);
        setShowScrollButton(false);
      } else {
        // Show scroll button if user scrolled up more than 100px from bottom
        if (scrollHeight - scrollTop - clientHeight > 100) {
          setShowScrollButton(true);
        }
      }
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    if (sessionExpired) {
      navigate(`/${tenantSlug}`, { replace: true });
    }
  };

  const handleFieldErrors = (fieldErrorsFromResponse) => {
    if (!fieldErrorsFromResponse || !Array.isArray(fieldErrorsFromResponse) || fieldErrorsFromResponse.length === 0) {
      return false;
    }

    // Field mapping configuration: API field name -> form field name
    const fieldMapping = {
      'recipientAddress.address1': 'address',
      'recipientAddress.address2': 'addressLineTwo',
      'recipientAddress.city': 'city',
      'recipientAddress.state': 'state',
      'recipientAddress.postalCode': 'postalCode',
    };

    // Map field errors from API to form field names
    const mappedErrors = {};
    fieldErrorsFromResponse.forEach(fieldError => {
      const field = fieldError.field;
      let message = fieldError.message;
      
      // Skip if message is null or undefined
      if (!message) {
        return;
      }
      
      // Capitalize first letter of the message
      if (message.length > 0) {
        message = message.charAt(0).toUpperCase() + message.slice(1);
      }
      
      // Replace address1 with "Address" and address2 with "Address Line 2" in the message
      message = message.replace(/address1/gi, 'Address');
      message = message.replace(/address2/gi, 'Address Line 2');
      
      // Map API field names to form field names using the configuration
      const formFieldName = fieldMapping[field];
      if (formFieldName) {
        mappedErrors[formFieldName] = message;
      }
    });
    
    // If no errors were successfully mapped, return false to show the generic error modal
    if (Object.keys(mappedErrors).length === 0) {
      return false;
    }
    
    // Set field errors to display in the form
    setFieldErrors(mappedErrors);
    
    // Scroll to the form to show errors
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    return true;
  };

  useEffect(() => {
    // If there's no state or customerDetails data, redirect to home
    if (
      !location.state?.from ||
      location.state?.from !== 'property-info-form' ||
      !location.state?.data?.customerDetails
    ) {
      navigate('/', { replace: true });
      return;
    }

    // Store JWT token from location state
    if (location.state?.jwtToken) {
      setJwtToken(location.state.jwtToken);
    }

    // This prevents forward navigation from working
    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, []);

  useEffect(() => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // If coming from property-info-form with data, allow access
    if (
      location.state?.from === 'property-info-form' &&
      location.state?.data?.customerDetails
    ) {
      const initialData = location.state.data.customerDetails;
      const initialCustomerDetails = {
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        address: initialData.address || '',
        addressLineTwo: initialData.addressLineTwo || '',
        city: initialData.city || '',
        state: initialData.state || '',
        postalCode: initialData.postalCode || '',
        balance: initialData.balance || 'NA',
      };
      setCustomerDetails(initialCustomerDetails);
      setFormData(initialCustomerDetails);
    }
  }, [location.state, navigate, tenantSlug]);

  useEffect(() => {
    const contentEl = termsContentRef.current;
    if (contentEl) {
      contentEl.addEventListener('scroll', handleScroll);
      // Check if content is already at bottom or doesn't need scrolling
      if (contentEl.scrollHeight <= contentEl.clientHeight) {
        setCanAgree(true);
      }
      return () => contentEl.removeEventListener('scroll', handleScroll);
    }
  }, [showTermsModal]);

  const handleFinalSubmit = async () => {
    if (!ownershipChecked) {
      alert('Please confirm ownership of the property');
      return;
    }

    if (!termsChecked) {
      alert('Please agree to the Terms of Use');
      return;
    }

    // Check if any address fields were modified
    const isFormModified =
      formData.address !== customerDetails?.address ||
      formData.addressLineTwo !== customerDetails?.addressLineTwo ||
      formData.city !== customerDetails?.city ||
      formData.state !== customerDetails?.state ||
      formData.postalCode !== customerDetails?.postalCode;

    setIsSubmitting(true);

    try {
      const payload = {
        updateRecipientAddress: isFormModified,
        recipientAddress: {
          address1: formData.address,
          address2: formData.addressLineTwo,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: 'US',
        },
      };
      const response = await requestCheque(payload, tenantSlug, jwtToken);
      if (
        response.status === 200 &&
        response.data.checkRequestStatus === 'Request check succeeded'
      ) {
        navigate(`/${tenantSlug}/request-processed`, { replace: true });
      } else {
        setErrorMessage('Failed to process request. Please try again.');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Submission error:', error?.message);
      const status = error?.response?.status;
      const errorFromResponse = error?.response?.data?.message;
      const fieldErrorsFromResponse = error?.response?.data?.fieldErrors;
      
      // Check if there are field-level validation errors
      const hasFieldErrors = handleFieldErrors(fieldErrorsFromResponse);
      
      if (!hasFieldErrors) {
        // Handle other types of errors with modal
        if (
          status === 401 &&
          (errorFromResponse === 'JWT token is missing in cookie' || 
           errorFromResponse === 'JWT token is missing' ||
           errorFromResponse?.includes('JWT'))
        ) {
          // Handle JWT token missing error
          setErrorMessage(
            'Your session has expired. Please refresh the page and try again.'
          );
          setSessionExpired(true);
          setShowErrorModal(true);
        } else if (errorFromResponse) {
          setErrorMessage(errorFromResponse);
          setShowErrorModal(true);
        } else {
          setErrorMessage('An unexpected error occurred. Please try again.');
          setShowErrorModal(true);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = useMemo(
    () => ownershipChecked && termsChecked && formData,
    [ownershipChecked, termsChecked, formData]
  );

  return (
    <div>
      <LoaderComponent showloader={isSubmitting} />
      <Banner
        bannerImage={DollarLogo}
        showPropertyAmount={true}
        amount={customerDetails?.balance}
      />
      <Container className="confirm-address-container">
        <div className="confirm-address-content">
          <h2 className="page-title">
            {en.confirm_address_title ||
              'Please confirm your mailing address for your unclaimed property check:'}
          </h2>

          {/* Form */}
          <CustomForm
            onSubmit={handleFinalSubmit}
            initialData={customerDetails}
            formRef={formRef}
            formData={formData}
            setFormData={setFormData}
            externalErrors={fieldErrors}
            onErrorsCleared={() => setFieldErrors({})}
          />

          {/* Checkboxes */}
          <div className="checkbox-section">
            <Form.Check
              type="checkbox"
              id="ownership-checkbox"
              className="custom-checkbox ownership-checkbox"
              checked={ownershipChecked}
              onChange={(e) => setOwnershipChecked(e.target.checked)}
              label="I am entitled to the above referenced property and acknowledge this property is not abandoned and I maintain ownership."
            />

            <Form.Check
              type="checkbox"
              id="terms-checkbox"
              className="custom-checkbox terms-checkbox"
              checked={termsChecked}
              onChange={(e) => setTermsChecked(e.target.checked)}
              label={
                <>
                  I have read and agree to the{' '}
                  <a href="#" onClick={handleShowTerms}>
                    Terms of Use
                  </a>
                </>
              }
            />
          </div>

          {/* Warning message */}
          <div className="warning-message">
            <img
              src={WarningIcon}
              alt="Warning icon"
              className="warning-icon"
            />
            <span className="warning-text">
              You will not be able to cancel once you select to request a check.
              Please review before submitting.
            </span>
          </div>

          {/* Submit button */}
          <div className="submit-button-wrapper">
            <Button
              type="button"
              className="request-check-button"
              disabled={!isFormValid || isSubmitting}
              onClick={() => {
                if (formRef.current) {
                  formRef.current.requestSubmit();
                }
              }}
            >
              {isSubmitting ? 'Processing...' : 'Request Check'}
            </Button>
          </div>
        </div>
      </Container>

      {/* Terms of Use Modal */}
      <CustomModal
        show={showTermsModal}
        onHide={handleCloseTerms}
        header="Terms of Use"
        size="lg"
        scrollable
        footer={
          <>
            <CustomButton
              variant="primary"
              onClick={handleAgreeTerms}
              disabled={!canAgree}
              className="agree-btn"
            >
              Agree
            </CustomButton>
          </>
        }
      >
        <div
          ref={termsContentRef}
          onScroll={handleScroll}
          className="terms-content-wrapper"
        >
          <TermsOfUse />
          {showScrollButton && (
            <div className="scroll-to-bottom-container">
              <CustomButton
                variant="secondary"
                onClick={handleScrollToBottom}
                className="scroll-to-bottom-btn"
              >
                Scroll to Bottom
              </CustomButton>
            </div>
          )}
        </div>
      </CustomModal>
      {/* Error Modal */}
      <CustomModal
        show={showErrorModal}
        onHide={handleCloseErrorModal}
        size="md"
        scrollable
        dialogClassName="modal-error-info"
      >
        <div>
          <p className="error-modal-header">Error</p>
          <p className="error-modal-message">{errorMessage}</p>
          <CustomButton onClick={handleCloseErrorModal} variant="full-width">
            {sessionExpired ? 'Go Back' : 'Close'}
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
};

export default ConfirmAddress;
